import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import hexToRgbChannels from '../../assets/js/hexToRgbChannels';
import '../../assets/styles/GradientCard.css';

const SPRING = { type: 'spring', stiffness: 400, damping: 15 };

const GradientCard = React.forwardRef(
  (
    {
      accent = '#7C3AED',
      badgeText,
      title,
      description,
      ctaText,
      ctaTo,
      ctaHref,
      icon,
      imageUrl,
      className = '',
      ...props
    },
    ref
  ) => {
    const reduced = useReducedMotion() === true;

    const cta = ctaText && (ctaTo || ctaHref) ? (
      <span className="gradient-card__cta">
        {ctaText}
        <ArrowRight className="gradient-card__arrow" size={16} aria-hidden="true" />
      </span>
    ) : null;

    return (
      <motion.article
        ref={ref}
        className={`gradient-card ${className}`.trim()}
        style={{ '--card-rgb': hexToRgbChannels(accent) || undefined }}
        variants={
          reduced
            ? undefined
            : { rest: { scale: 1, y: 0 }, hover: { scale: 1.02, y: -4 } }
        }
        initial={reduced ? undefined : 'rest'}
        animate={reduced ? undefined : 'rest'}
        whileHover={reduced ? undefined : 'hover'}
        {...props}
      >
        {(icon || imageUrl) && (
          <motion.span
            aria-hidden="true"
            className={`gradient-card__art ${icon ? 'gradient-card__art--icon' : ''}`}
            variants={
              reduced
                ? undefined
                : { rest: { scale: 1, rotate: 0 }, hover: { scale: 1.08, rotate: 3 } }
            }
            transition={reduced ? undefined : SPRING}
          >
            {icon || <img src={imageUrl} alt="" className="gradient-card__img" />}
          </motion.span>
        )}

        <div className="gradient-card__body">
          {badgeText && (
            <p className="gradient-card__badge">
              <span className="gradient-card__dot" aria-hidden="true" />
              {badgeText}
            </p>
          )}

          <div className="gradient-card__copy">
            <h3 className="gradient-card__title">{title}</h3>
            <p className="gradient-card__text">{description}</p>
          </div>

          {cta && ctaTo && (
            <Link to={ctaTo} className="gradient-card__link">{cta}</Link>
          )}
          {cta && !ctaTo && ctaHref && (
            <a
              href={ctaHref}
              className="gradient-card__link"
              target="_blank"
              rel="noopener noreferrer"
            >
              {cta}
            </a>
          )}
        </div>
      </motion.article>
    );
  }
);

GradientCard.displayName = 'GradientCard';

export default GradientCard;
export { GradientCard };
